import { useState, useEffect, useCallback } from 'react';
import { Pane, EditIcon, Text, Heading, Button, Dialog, TextInputField, TextareaField, toaster, DocumentIcon, SavedIcon } from 'evergreen-ui';
import { getUserDetails, saveAdminDetails } from '../../services/collaboratorServices';
import UserDetailsForm from './UserDetailsForm';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
    }
    const d = new Date(dateString);
    if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    return dateString;
};

const AdminDetailsView = ({ collaborator }) => {
    const [details, setDetails] = useState(null);
    const [isEditingAdmin, setIsEditingAdmin] = useState(false);
    const [isEditingFull, setIsEditingFull] = useState(false);
    const [adminForm, setAdminForm] = useState({ admin_data_ricezione: '', admin_verificato_da: '', admin_note: '', iban_aggiornato: 0, iban_data_aggiornamento: '' });
    const [loading, setLoading] = useState(false);

    const fetchDetails = useCallback(async () => {
        try {
            const data = await getUserDetails(collaborator.id);
            if (data && !data.error) {
                setDetails(data);
                setAdminForm({
                    admin_data_ricezione: data.admin_data_ricezione || '',
                    admin_verificato_da: data.admin_verificato_da || '',
                    admin_note: data.admin_note || '',
                    iban_aggiornato: data.iban_aggiornato || 0,
                    iban_data_aggiornamento: data.iban_data_aggiornamento || ''
                });
            }
        } catch(e) {
            console.error(e);
        }
    }, [collaborator]);

    useEffect(() => {
        if(collaborator?.id) {
            fetchDetails();
        }
    }, [collaborator, fetchDetails]);

    const handleSaveAdmin = async () => {
        setLoading(true);
        try {
            const resp = await saveAdminDetails({
                user_id: collaborator.id,
                ...adminForm
            });
            if (resp && resp.success) {
                toaster.success('Sezione amministrativa aggiornata.');
                setDetails(prev => ({...prev, ...adminForm}));
                setIsEditingAdmin(false);
            } else {
                toaster.danger('Errore:' + resp?.error);
            }
        } catch(e) {
            toaster.danger('Errore di connessione.');
        } finally {
            setLoading(false);
        }
    };

    if (!details || Object.keys(details).length <= 1) {
        return (
            <Pane padding={16}>
                <Text>Il dipendente non ha ancora compilato la sua scheda.</Text>
                <Pane marginTop={16}>
                    <Button intent="success" onClick={() => setIsEditingFull(true)}>Compila al posto suo</Button>
                </Pane>
                <Dialog isShown={isEditingFull} title={`Compila scheda: ${collaborator.nome} ${collaborator.cognome}`} onCloseComplete={() => setIsEditingFull(false)} hasFooter={false} width={800} shouldAutoFocus={false}>
                    <UserDetailsForm userId={collaborator.id} isDirector={true} baseUser={collaborator} onSaved={() => {
                        fetchDetails();
                        setIsEditingFull(false);
                    }} />
                </Dialog>
            </Pane>
        );
    }

    const renderLink = (url, name) => {
        if (!url) return <Text color="muted">Non allegato</Text>;
        return <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', overflowWrap: 'anywhere', wordBreak: 'break-all', maxWidth: '100%' }}><DocumentIcon /> {name}</a>
    };

    return (
        <Pane 
            padding={16} 
            background="tint1" 
            borderRadius={4} 
            border="default" 
            marginTop={8} 
            marginBottom={16}
            style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', maxWidth: '100%', overflowX: 'hidden' }}
        >
            <Heading size={500} marginBottom={16}>Scheda completa dipendente</Heading>

            <Pane display="flex" gap={16} flexWrap="wrap" marginBottom={24}>
                <Pane flex={1} minWidth={200} style={{ maxWidth: '100%' }}>
                    <Heading size={400} borderBottom="1px solid #E4E7EB" paddingBottom={4} marginBottom={8}>Anagrafica</Heading>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>CF:</b> {details.cf || '-'}</Text>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Nato/a il:</b> {formatDate(details.data_nascita)} a {details.luogo_nascita || '-'}</Text>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Residente in:</b> {details.indirizzo_residenza || '-'}, {details.comune || '-'} ({details.provincia || '-'}) {details.cap || '-'}</Text>
                </Pane>
                <Pane flex={1} minWidth={200} style={{ maxWidth: '100%' }}>
                    <Heading size={400} borderBottom="1px solid #E4E7EB" paddingBottom={4} marginBottom={8}>Contatti</Heading>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Tel:</b> {details.telefono_personale || '-'} {details.telefono_alternativo ? ` / ${details.telefono_alternativo}` : ''}</Text>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Email:</b> {details.email_personale || '-'} / <b>PEC:</b> {details.pec || '-'}</Text>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Consenso comunicazioni:</b> {details.consenso_comunicazioni ? 'SÌ' : 'NO'}</Text>
                    
                    <Heading size={400} marginTop={16} borderBottom="1px solid #E4E7EB" paddingBottom={4} marginBottom={8}>Emergenza</Heading>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Da contattare:</b> {details.emergenza_nome || '-'} ({details.emergenza_relazione || '-'})</Text>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Al numero:</b> {details.emergenza_telefono || '-'}</Text>
                </Pane>
                <Pane flex={1} minWidth={200} style={{ maxWidth: '100%' }}>
                    <Heading size={400} borderBottom="1px solid #E4E7EB" paddingBottom={4} marginBottom={8}>Inquadramento</Heading>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Tipologia contratto:</b> {details.tipo_contratto === 'altro' ? details.note_tipo_contratto : details.tipo_contratto || '-'}</Text>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Assunto dal:</b> {formatDate(details.data_assunzione)}</Text>
                    { (details.tipo_contratto === 'tempo_determinato' || details.tipo_contratto === 'apprendistato') && (
                        <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Termine rapporto:</b> {formatDate(details.termine_rapporto)}</Text>
                    )}
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Orario:</b> {details.orario_lavoro === 'part_time' ? `Part time (${details.ore_part_time} ore)` : details.orario_lavoro || '-'}</Text>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>CCNL:</b> UNEBA</Text>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Livello inq.:</b> {details.livello_inquadramento || '-'}</Text>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Ore mensili prev.:</b> {details.ore_mensili_previste ? parseFloat(details.ore_mensili_previste) : '-'}</Text>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Ore giornaliere prev.:</b> {details.ore_giornaliere_previste ? parseFloat(details.ore_giornaliere_previste) : '-'}</Text>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Mansione:</b> {details.mansione || '-'}</Text>
                    <Text display="block" style={{ overflowWrap: 'anywhere' }}><b>Sede:</b> {details.sede_lavoro || '-'}</Text>
                </Pane>
            </Pane>

            <Heading size={400} borderBottom="1px solid #E4E7EB" paddingBottom={4} marginBottom={8}>Documentazione (Files e IBAN)</Heading>
            <Pane display="flex" flexDirection="column" gap={8} marginBottom={24}>
                <Text style={{ wordBreak: 'break-all' }}><b>IBAN:</b> {details.iban || '-'}</Text>
                <Text style={{ wordBreak: 'break-all' }}><b>Intestatario:</b> {details.iban_intestatario || '-'}</Text>
                <Text style={{ wordBreak: 'break-all' }}><b>Banca:</b> {details.iban_banca || '-'}</Text>
                {details.doc_identita_url && <Text><b>Documento d'identità:</b> {renderLink(details.doc_identita_url, 'Apri doc. Identità')}</Text>}
                {details.doc_cf_url && <Text><b>Codice fiscale:</b> {renderLink(details.doc_cf_url, 'Apri Codice Fiscale')}</Text>}
                {details.doc_sanitaria_url && <Text><b>Certificato sanitario:</b> {renderLink(details.doc_sanitaria_url, 'Apri Doc. Sanitaria')}</Text>}
                {details.doc_casellario_url && <Text><b>Casellario giudiziale:</b> {renderLink(details.doc_casellario_url, 'Apri Casellario')}</Text>}
                {details.doc_carichi_url && <Text><b>Carichi pendenti:</b> {renderLink(details.doc_carichi_url, 'Apri Carichi')}</Text>}
                {details.doc_altro_url && <Text><b>Documento storico ({details.doc_altro_desc || ''}):</b> {renderLink(details.doc_altro_url, 'Apri file')}</Text>}

                {details.attachments && details.attachments.length > 0 && (
                    <Pane marginTop={8}>
                        <Text><b>Allegati recenti:</b></Text>
                        <ul style={{ paddingLeft: 20, margin: 0, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                            {details.attachments.map(att => (
                                <li key={att.id} style={{ marginBottom: '8px' }}>
                                    <Pane display="flex" alignItems="center" gap={8} flexWrap="wrap">
                                        <Text style={{ wordBreak: 'break-word' }}>{renderLink(att.file_path, att.file_name)}</Text>
                                    </Pane>
                                </li>
                            ))}
                        </ul>
                    </Pane>
                )}
            </Pane>

            <Heading size={400} borderBottom="1px solid #E4E7EB" paddingBottom={4} marginBottom={8}>Dichiarazioni e privacy</Heading>
            <Pane marginBottom={24} display="flex" flexDirection="column" gap={8}>
                <Text>Il/La dipendente {details.letto_privacy_policy ? <b>HA LETTO</b> : <b>NON HA LETTO</b>} l'Informativa Privacy.</Text>
                <Text>Il/La dipendente {details.dichiara_veritieri ? <b>HA FIRMATO e dichiarato</b> : <b>NON HA dichiarato</b>} la veridicità dei dati (Data: {formatDate(details.data_dichiarazione)}).</Text>
            </Pane>

            <Heading size={400} borderBottom="1px solid #E4E7EB" paddingBottom={4} marginBottom={8}>Autocertificazione Carichi Penali (D.P.R. 445/2000)</Heading>
            <Pane marginBottom={24} display="flex" flexDirection="column" gap={8}>
                <Text>{details.cp_nessuna_condanna ? '✅' : '❌'} Assenza condanne penali (artt. 600-bis, 600-ter, 600-quater, 600-quinquies, 609-undecies c.p. e sanzioni interdittive per attività con minori)</Text>
                <Text>{details.cp_nessun_carico_pendente ? '✅' : '❌'} Assenza carichi pendenti e procedimenti penali in corso</Text>
                <Text>{details.cp_residente_circondario ? '✅' : '❌'} Residente nel circondario della Procura della Repubblica competente</Text>
            </Pane>

            <Heading size={400} marginTop={16} borderBottom="1px solid #E4E7EB" paddingBottom={4} marginBottom={8} color="danger">Sezione riservata all'amministrazione (Riservata a te)</Heading>
            <Pane background="#FDF8F6" padding={16} borderRadius={4} display="flex" flexDirection="column" gap={8}>
                {isEditingAdmin ? (
                    <Pane display="flex" flexDirection="column" gap={12}>
                        <TextInputField label="Data ricezione modulo" type="date" value={adminForm.admin_data_ricezione} onChange={e => setAdminForm(p => ({...p, admin_data_ricezione: e.target.value}))} />
                        <TextInputField label="Documento verificato da" placeholder="Es. Nome e cognome direttore" value={adminForm.admin_verificato_da} onChange={e => setAdminForm(p => ({...p, admin_verificato_da: e.target.value}))} />
                        <TextareaField label="Note interne (Amm/Direzione)" placeholder="Qualsiasi avvertenza..." value={adminForm.admin_note} onChange={e => setAdminForm(p => ({...p, admin_note: e.target.value}))} />

                        <Pane display="flex" gap={8} marginTop={8}>
                            <Button intent="success" isLoading={loading} onClick={handleSaveAdmin}>Salva dati admin</Button>
                            <Button onClick={() => setIsEditingAdmin(false)}>Annulla</Button>
                        </Pane>
                    </Pane>
                ) : (
                    <>
                        <Pane display="flex" flexDirection="column" gap={4}>
                            <Text display="block"><b>Data ricezione:</b> {formatDate(details.admin_data_ricezione)}</Text>
                            <Text display="block" wordWrap="break-word"><b>Verificato da:</b> {details.admin_verificato_da || '-'}</Text>
                            <Text display="block" wordWrap="break-word"><b>Note interne:</b> {details.admin_note || '-'}</Text>
                            {(details.iban_aggiornato === 1 || details.iban_aggiornato === true || details.iban_aggiornato === '1') ? (
                                <Text display="block" color="success">
                                    <SavedIcon color="success" size={12} marginRight={4} />
                                    <b>IBAN aggiornato per versamento stipendio</b> 
                                    {details.iban_data_aggiornamento ? ` (alla data: ${formatDate(details.iban_data_aggiornamento)})` : ''}
                                </Text>
                            ) : (
                                <Text display="block" color="danger"><b>IBAN valido non ancora registrato.</b></Text>
                            )}
                        </Pane>
                        <Pane marginTop={8}>
                            <Button iconBefore="edit" onClick={() => setIsEditingAdmin(true)}>Valida dati <EditIcon marginLeft={8} /></Button>
                        </Pane>
                    </>
                )}
            </Pane>

              <Pane marginTop={24} display="flex" justifyContent="flex-start">
                   <Button intent="success" onClick={() => setIsEditingFull(true)}>Modifica intera scheda <EditIcon marginLeft={8} /></Button>
              </Pane>

              <Dialog isShown={isEditingFull} title={`Modifica scheda: ${collaborator.nome} ${collaborator.cognome}`} onCloseComplete={() => setIsEditingFull(false)} hasFooter={false} width={800} shouldAutoFocus={false}>
                  <UserDetailsForm userId={collaborator.id} isDirector={true} baseUser={collaborator} onSaved={() => {
                      fetchDetails();
                      setIsEditingFull(false);
                  }} />
              </Dialog>
          </Pane>
      );
};export default AdminDetailsView;
